import type {
  ChatMessage,
  ChatRole,
  CreateSynthesisSessionInput,
  ReferenceResult,
  SynthesisAskResult,
  SynthesisConfiguration,
  SynthesisSession,
  SynthesisSessionSummary,
} from "../../domain/entities/synthesis-session";
import type {
  AskSessionResponseDataModel,
  ConfigurationModel,
  ReferenceResultModel,
  SessionListModel,
  SessionMessageModel,
  SessionModel,
} from "../models/synthesis-session-model";

const inferRole = (m: SessionMessageModel, index: number): ChatRole => {
  const r = m.role?.toLowerCase();
  if (r === "user" || r === "human") return "user";
  if (r === "assistant" || r === "ai" || r === "model" || r === "system")
    return "assistant";
  return index % 2 === 0 ? "user" : "assistant";
};

const toMessage = (m: SessionMessageModel, index: number): ChatMessage => ({
  id: m._id || `msg-${index}`,
  role: inferRole(m, index),
  content: m.message,
  references: Array.isArray(m.references) ? m.references.map(toReferenceResult) : [],
  followUpQuestions: Array.isArray(m.follow_up_questions) ? m.follow_up_questions : [],
  consulted: Array.isArray(m.consulted) ? m.consulted.map(toReferenceResult) : [],
});

const toConfiguration = (c: ConfigurationModel): SynthesisConfiguration => ({
  model: c.model ?? "",
  knowledgeBaseIds: c.knowledge_bases ?? [],
  enableWebSearch: c.enable_web_search ?? false,
});

const toReferenceContext = (
  c: ReferenceResultModel["context"],
): ReferenceResult["context"] => {
  if (!c) return undefined;
  const preceding = typeof c.preceding === "string" ? c.preceding : undefined;
  const following = typeof c.following === "string" ? c.following : undefined;
  const enrichment = typeof c.enrichment === "string" ? c.enrichment : undefined;
  if (!preceding && !following && !enrichment) return undefined;
  return { preceding, following, enrichment };
};

const toReferenceResult = (r: ReferenceResultModel): ReferenceResult => ({
  id: r.id ?? "",
  documentId: r.document_id ?? "",
  docName: r.doc_name ?? "",
  kbId: r.kb_id ?? "",
  title: r.title ?? "",
  breadcrumb: r.breadcrumb ?? "",
  content: r.content ?? "",
  score: r.score ?? 0,
  // Reloaded pre-Batch-A sessions carry no `kind` — treat as a plain chunk.
  kind: r.kind ?? "chunk",
  context: toReferenceContext(r.context),
});

export const toSynthesisAskResult = (data: unknown): SynthesisAskResult => {
  const d = data as AskSessionResponseDataModel;
  return {
    answer: d.answer ?? "",
    references: Array.isArray(d.references)
      ? d.references.map(toReferenceResult)
      : [],
    followUpQuestions: Array.isArray(d.follow_up_questions)
      ? d.follow_up_questions
      : [],
    consulted: Array.isArray(d.consulted) ? d.consulted.map(toReferenceResult) : [],
  };
};

export const toSynthesisSession = (model: SessionModel): SynthesisSession => {
  return {
    id: model._id,
    userId: model.user_id,
    title: model.topic ?? model.name,
    messages: (model.conversation_history ?? []).map(toMessage),
    configuration: model.configuration
      ? toConfiguration(model.configuration)
      : undefined,
    createdAt: model.created_at,
    updatedAt: model.updated_at,
  };
};

const summaryTitle = (s: SessionModel, id: string): string => {
  if (s.topic?.trim()) return s.topic;
  if (s.name?.trim()) return s.name;
  const first = s.conversation_history?.[0];
  if (first?.message?.trim()) {
    const t = first.message.trim();
    return t.length > 64 ? `${t.slice(0, 61)}…` : t;
  }
  return `Session ${id.slice(0, 8)}…`;
};

export const toSynthesisSessionSummary = (
  model: SessionModel,
): SynthesisSessionSummary => {
  const id = model._id;
  if (!id) {
    throw new Error("Session list item is missing an id");
  }
  return {
    id: model._id,
    title: summaryTitle(model, id),
    updatedAt: model.updated_at ?? model.created_at,
  };
};

const normalizeListPayload = (data: unknown): SessionModel[] => {
  if (Array.isArray(data)) {
    return data as SessionModel[];
  }
  const obj = data as Partial<SessionListModel> & {
    results?: SessionModel[];
    sessions?: SessionModel[];
  };
  if (Array.isArray(obj.items)) return obj.items;
  if (Array.isArray(obj.sessions)) return obj.sessions;
  if (Array.isArray(obj.results)) return obj.results;
  return [];
};

export const toSynthesisSessionSummaryList = (data: unknown) =>
  normalizeListPayload(data).map(toSynthesisSessionSummary);

export const toCreateRequestBody = (input: CreateSynthesisSessionInput) => {
  const body: {
    message: { type: "text"; content: string };
    configuration?: {
      model: string;
      knowledge_bases: string[];
      enable_web_search: boolean;
    };
  } = {
    message: { type: "text", content: input.content },
  };
  if (input.configuration) {
    const c = input.configuration;
    body.configuration = {
      model: c.model ?? "",
      knowledge_bases: c.knowledgeBaseIds ?? [],
      enable_web_search: c.enableWebSearch ?? false,
    };
  }
  return body;
};

export const toUpdateRequestBody = (input: {
  model?: string | null;
  knowledgeBaseIds?: string[];
  enableWebSearch?: boolean;
}): Record<string, unknown> => {
  const o: Record<string, unknown> = {};
  if ("model" in input) o.model = input.model;
  if (input.knowledgeBaseIds !== undefined) {
    o.knowledge_bases = input.knowledgeBaseIds;
  }
  if (input.enableWebSearch !== undefined) {
    o.enable_web_search = input.enableWebSearch;
  }
  return o;
};
